import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import { useEditorPasteDetection } from '@confluence/editor-features/entry-points/useEditorPasteDetection';
import { useTitleGenerationStreaming } from '@confluence/editor-features/entry-points/useTitleGenerationStreaming';

interface EditorTitleAIGenerationOnPasteProps {
	editorAPI?: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	contentId: string;
	cloudId: string;
	title: string | null;
	userOffline?: boolean;
	onTitleGenerated: (title: string) => void;
	onTitleGeneratingChange?: (isGenerating: boolean) => void;
}

export const EditorTitleAIGenerationOnPaste: React.FC<EditorTitleAIGenerationOnPasteProps> = ({
	editorAPI,
	contentId,
	cloudId,
	title,
	userOffline,
	onTitleGenerated,
	onTitleGeneratingChange,
}) => {
	const {
		shouldShowStreaming,
		documentContext,
		onStreamingComplete,
		onStreamingError,
		isTitleGenerating,
	} = useEditorPasteDetection({
		editorAPI,
		contentId,
		cloudId,
		title,
		onTitleChange: onTitleGenerated,
		userOffline,
	});

	const { locale } = useIntl();

	const hasCompletedRef = useRef(false);
	const prevIsTitleGeneratingRef = useRef(isTitleGenerating);

	useEffect(() => {
		if (isTitleGenerating && !prevIsTitleGeneratingRef.current) {
			hasCompletedRef.current = false;
		}
		prevIsTitleGeneratingRef.current = isTitleGenerating;
	}, [isTitleGenerating]);

	const { content, responseState, error } = useTitleGenerationStreaming({
		contentId,
		cloudId,
		documentContext,
		locale,
		skip: !shouldShowStreaming,
		onTitleChange: (newTitle) => {
			if (!hasCompletedRef.current) {
				hasCompletedRef.current = true;
				onTitleGenerated(newTitle);
			}
		},
	});

	useEffect(() => {
		if (responseState === 'FinalResponse' && content) {
			onStreamingComplete();
		} else if (responseState === 'Error' && error) {
			onStreamingError();
		}
	}, [responseState, content, error, onStreamingComplete, onStreamingError]);

	useEffect(() => {
		onTitleGeneratingChange?.(isTitleGenerating);
	}, [isTitleGenerating, onTitleGeneratingChange]);

	return null;
};
